import useEmblaCarousel, { type EmblaOptionsType } from 'embla-carousel-react'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { useTranslation } from 'next-i18next'
import { useCallback, useEffect, useState } from 'react'

import { ClientRender } from '@/components/shared/client-render'
import { MIN_ADS_COUNT, SHOPPING_ADS, SMALL_SCREEN_MAX_ADS } from '@/constants/others'
import { type CasListing } from '@/generated'
import { useMediaQuery } from '@/hooks/client-only/useMediaQuery'
import { ROUTE_LOCALE, useLocale } from '@/hooks/useLocale'
import { MEDIA_QUERIES } from '@/ui/constants/mediaQueries'
import { ListingTile } from '@/ui/molecules/listing-tile'
import { theme } from '@/ui/theme/theme'

import { DotButton, NextButton, PrevButton } from './ShoppingAdsBtn'
import { Container, Dots, Heading, List, ListingTileImage, Viewport, Wrapper } from './styled'
import { getMinimumAdsForScreenSize } from './utils/getMinimumAdsForScreenSize'
import { GetPriceLabelValue } from './utils/GetPriceLabelValue'

export type ShoppingAdsCarouselProps = {
  shoppingAdsListing: CasListing[]
  minAds?: number
  maxAds?: number
  handleShoppingAdClick?: (id: string, pos: number) => void
  handleShoppingAdScrollPrev?: () => void
  handleShoppingAdScrollNext?: () => void
}

const ShoppingAdsCarouselInner = ({
  shoppingAdsListing,
  minAds = SHOPPING_ADS.MIN,
  maxAds = SHOPPING_ADS.MAX,
  handleShoppingAdClick,
  handleShoppingAdScrollPrev,
  handleShoppingAdScrollNext,
}: ShoppingAdsCarouselProps) => {
  const [prevBtnEnabled, setPrevBtnEnabled] = useState(false)
  const [nextBtnEnabled, setNextBtnEnabled] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([])
  const { isSmallScreen, isMediumScreen, isLargeScreen } = useMediaQuery()
  const { routeLocale } = useLocale()

  const isFrench = routeLocale === ROUTE_LOCALE.fr

  /**
   * To get minimum Ads depending on screen size small, medium and large - as 2, 4 or 5
   */
  const numberMinAds = getMinimumAdsForScreenSize({
    isSmall: isSmallScreen,
    isMedium: isMediumScreen,
    isLarge: isLargeScreen,
  })

  const numberMaxAds = isSmallScreen ? SMALL_SCREEN_MAX_ADS : maxAds

  const { t } = useTranslation('srp')

  // Limiting the amount of Maximum Ads
  shoppingAdsListing = shoppingAdsListing?.slice(0, numberMaxAds)

  const showcaseOptions: EmblaOptionsType = {
    active: shoppingAdsListing?.length === numberMinAds ? false : true,
    slidesToScroll: numberMinAds,
    inViewThreshold: 0.5,
    breakpoints: {
      [MEDIA_QUERIES(theme).small]: { slidesToScroll: numberMinAds },
      [MEDIA_QUERIES(theme).medium]: { slidesToScroll: MIN_ADS_COUNT.MEDIUM_SCREEN },
    },
  }

  const [emblaRef, emblaApi] = useEmblaCarousel(showcaseOptions, [WheelGesturesPlugin()])

  useEffect(() => {
    if (!emblaApi) return

    emblaApi.reInit()
  }, [emblaApi, shoppingAdsListing, isSmallScreen, isMediumScreen])

  const onSelect = useCallback(() => {
    if (!emblaApi) return
    setSelectedIndex(emblaApi.selectedScrollSnap())
    setPrevBtnEnabled(emblaApi.canScrollPrev())
    setNextBtnEnabled(emblaApi.canScrollNext())
  }, [emblaApi, setSelectedIndex])

  useEffect(() => {
    if (!emblaApi) return
    onSelect()
    setScrollSnaps(emblaApi.scrollSnapList())
    emblaApi.on('select', onSelect)
    emblaApi.on('reInit', onSelect)
  }, [emblaApi, setScrollSnaps, onSelect, isSmallScreen, isMediumScreen])

  const scrollPrev = useCallback(() => {
    emblaApi?.scrollPrev()
    handleShoppingAdScrollPrev?.()
  }, [emblaApi, handleShoppingAdScrollPrev])
  const scrollNext = useCallback(() => {
    emblaApi?.scrollNext()
    handleShoppingAdScrollNext?.()
  }, [emblaApi, handleShoppingAdScrollNext])
  const scrollTo = useCallback((index: number) => emblaApi && emblaApi.scrollTo(index), [emblaApi])

  //todo: Uncomment once the anvil changes are done for listing price types for KAS
  /**
   * Renders a shopping ads price label. Displays price if available, otherwise displays nothing.
   *
   * @param {Object} params The function parameters.
   * @param {Object} params.amount The price amount to be displayed.
   * @param {boolean} [params.isFrench] A flag indicating if the price should be formatted for French locale (optional).
   * @returns {JSX.Element | null} The rendered price label component or null if the price amount is 0.
   */

  return shoppingAdsListing.length >= numberMinAds ? (
    <Wrapper data-testid="shopping-ads-carousel">
      <Container>
        <Heading as="h2" data-testid="shopping-ads-title">
          {t('listing.shopping_ads.title_section')}
        </Heading>
        <Viewport ref={emblaRef}>
          <List items={numberMinAds}>
            {shoppingAdsListing?.map((image, i) => (
              <ListingTile
                as="li"
                key={`shopping-ads-${i}`}
                data-testid={`shopping-ad-${i}`}
                title={image.title}
                onClick={() => handleShoppingAdClick?.(image.id, ++i)}
                link={image.url.toString()}
                image={
                  <ListingTileImage
                    src={image.imageUrls[0]?.toString()}
                    alt={image.title}
                    layout="fill"
                    lazyBoundary="600px"
                    unoptimized
                  />
                }
                pricingLabel={
                  <GetPriceLabelValue
                    price={image.price}
                    priceType={image.price.type}
                    isFrench={isFrench}
                    translation={t}
                  />
                }
              />
            ))}
          </List>
          <PrevButton onClick={scrollPrev} enabled={prevBtnEnabled} />
          <NextButton onClick={scrollNext} enabled={nextBtnEnabled} />
        </Viewport>

        {scrollSnaps.length > 1 && (
          <Dots>
            {scrollSnaps.map((_, index) => (
              <DotButton
                index={index}
                minAds={minAds}
                key={`shopping-ads-dot-${index}`}
                selected={index === selectedIndex}
                onClick={() => scrollTo(index)}
              />
            ))}
          </Dots>
        )}
      </Container>
    </Wrapper>
  ) : null
}

export const ShoppingAdsCarousel = (props: ShoppingAdsCarouselProps) => {
  return (
    <ClientRender>
      <ShoppingAdsCarouselInner {...props} />
    </ClientRender>
  )
}
